import CompanyLayout from "../company/CompanyLayout";

/* The company dashboard as a visitor sees it before they have one: the REAL
   shell — same CompanyLayout, same sidebar, same cards — filled with static
   placeholder data. No auth, no API calls, no writes. Rendered inert by the
   caller (pointer-events: none) so nobody clicks through into a protected
   route.

   Lives here rather than inside a page because two flows show it: the
   get-started teaser and the need-first signup's "your post is ready" reveal.
   Importing it from a page would make those two modules circular. */

const SERIF = "'Bodoni Moda', serif";

// A stand-in org. CompanyLayout trusts a passed org (skips its /me fetch),
// and CompanySidebar reads these permission flags to decide what to show —
// so flip them all on for a full, realistic nav. is_subscribed hides the
// "Subscription Required" nudge.
export const DEMO_ORG = {
  name: "Your Company",
  role_display: "Owner",
  is_org_wide: true,
  is_subscribed: true,
  w2_features_enabled: true,
  revenue_tracking_enabled: true,
  can_view_team_page: true,
  can_view_org_management: true,
  can_view_income_statement: true,
  can_view_stripe_settings: true,
  can_view_finance: true,
  can_view_fraud_alerts: true,
  can_view_talent_search: true,
  can_draft_contracts: true,
};

const SUMMARY = [
  { label: "Monthly Spend (Last 30 Days)", value: "$18,400", emphasis: true },
  { label: "Active Contracts (Fully Signed)", value: 8 },
  { label: "In Negotiation (Not Fully Signed)", value: 3 },
  { label: "Team Members", value: 12 },
];

const TIMECARDS = [
  { name: "Marcus Bell", date: "2026-07-24", hours: "8.00", work: 320, fee: 12.8, total: 332.8 },
  { name: "Priya Nair", date: "2026-07-24", hours: "6.50", work: 260, fee: 10.4, total: 270.4 },
];

function SummaryCard({ label, value, emphasis }) {
  if (emphasis) {
    return (
      <div className="p-5 rounded-2xl border" style={{ background: "#1B1813", borderColor: "#1B1813", color: "var(--on-dark,#FAF8F2)" }}>
        <div className="font-mono uppercase text-[0.625rem] tracking-[.16em]" style={{ color: "rgba(250,248,242,.6)" }}>{label}</div>
        <div className="mt-1 font-semibold" style={{ fontFamily: SERIF, fontSize: "1.875rem" }}>{value}</div>
      </div>
    );
  }
  return (
    <div className="bg-surface border border-line p-5 rounded-2xl shadow-sift-sm">
      <div className="font-mono uppercase text-[0.625rem] tracking-[.16em] text-tertiary">{label}</div>
      <div className="mt-1 font-semibold text-ink" style={{ fontFamily: SERIF, fontSize: "1.875rem" }}>{value}</div>
    </div>
  );
}

export default function CompanyDashboardPreview({ orgName = "Your Company" }) {
  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h1 className="font-display italic text-ink text-2xl sm:text-3xl">{orgName}</h1>
      <span className="inline-flex items-center gap-1.5 rounded-pill bg-brand text-white px-4 py-2.5 text-sm font-semibold">
        Post a need
      </span>
    </div>
  );

  return (
    <CompanyLayout org={{ ...DEMO_ORG, name: orgName }} header={header}>
      {/* KPIs */}
      <section className="px-0 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SUMMARY.map((c) => (
            <SummaryCard key={c.label} label={c.label} value={c.value} emphasis={c.emphasis} />
          ))}
        </div>
      </section>

      {/* Unapproved Timecards */}
      <section className="bg-surface border border-line shadow-sift-sm rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold font-display italic text-ink">Unapproved Timecards</h2>
          <span className="text-sm text-brand">View All</span>
        </div>
        <div className="text-xs mb-2 mt-1 text-tertiary">{TIMECARDS.length} awaiting approval</div>
        <ul className="divide-y divide-line">
          {TIMECARDS.map((t) => (
            <li key={t.name} className="py-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-sm flex-1 min-w-0">
                  <div className="font-medium truncate text-ink">{t.name}</div>
                  <div className="text-xs text-tertiary">{t.date} • {t.hours} hrs</div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <span className="text-xs bg-brand text-white px-3 py-1 rounded-pill">Approve</span>
                  <span className="text-xs bg-red-600 text-white px-3 py-1 rounded-pill">Reject</span>
                </div>
              </div>
              <div className="bg-sunken rounded-lg p-2 text-xs space-y-1">
                <div className="flex justify-between text-tertiary"><span>Work:</span><span>${t.work.toFixed(2)}</span></div>
                <div className="flex justify-between text-tertiary"><span>Platform Fee (4%):</span><span>${t.fee.toFixed(2)}</span></div>
                <div className="flex justify-between font-semibold border-t pt-1 mt-1 text-ink border-line"><span>Total Cost:</span><span>${t.total.toFixed(2)}</span></div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Pending Travel */}
      <section className="bg-surface border border-line shadow-sift-sm rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold font-display italic text-ink">Pending Travel Requests</h2>
          <span className="text-sm text-brand">Review all</span>
        </div>
        <ul className="divide-y divide-line mt-2">
          <li className="py-3 flex items-center justify-between gap-3">
            <div className="text-sm flex-1 min-w-0">
              <div className="font-medium truncate text-ink">Dana Whitfield</div>
              <div className="text-xs text-tertiary">Indianapolis, IN → Chicago, IL • 2026-08-04–2026-08-06 • Est. $640</div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <span className="text-xs bg-brand text-white px-3 py-1 rounded-pill">Approve</span>
              <span className="text-xs bg-red-600 text-white px-3 py-1 rounded-pill">Reject</span>
            </div>
          </li>
        </ul>
      </section>

      {/* Find Talent CTA */}
      <section className="bg-surface border border-line shadow-sift-sm rounded-2xl p-5">
        <h2 className="text-xl font-semibold font-display italic text-ink">Find Talent</h2>
        <div className="text-sm mt-3 text-tertiary">
          Post what you need and browse talent and openings on the postings board.
        </div>
        <div className="mt-3">
          <span className="inline-block text-xs bg-brand text-white px-3 py-2 rounded-pill font-semibold">Go to Postings</span>
        </div>
      </section>
    </CompanyLayout>
  );
}
